/**
 * StudyClips — the moments a student kept to revise from.
 *
 * A clip is a marker, not a copy: the lesson, the second it starts and the
 * words that were said. Opening one goes straight back to that moment.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Bookmark, Trash2, Play, PencilLine } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Request failed');
  return body;
};

export const fmtClock = (s) => {
  const total = Math.floor(s || 0);
  const m = Math.floor(total / 60);
  return `${m}:${String(total % 60).padStart(2, '0')}`;
};

export const StudyClips = ({ tenantId }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [savingNote, setSavingNote] = React.useState(false);
  const clips = useQuery({
    queryKey: ['study-clips', tenantId],
    queryFn: () => api(`/school/tenants/${tenantId}/study-clips`),
    retry: false,
  });

  const saveNote = async (clip) => {
    if (savingNote) return;
    setSavingNote(true);
    try {
      await api(`/school/tenants/${tenantId}/study-clips/${clip.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ note: draft, label: clip.label || '' }),
      });
      toast.success('Note saved');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['study-clips', tenantId] });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingNote(false);
    }
  };

  const remove = useMutation({
    mutationFn: (id) => api(`/school/tenants/${tenantId}/study-clips/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Clip removed');
      qc.invalidateQueries({ queryKey: ['study-clips', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const rows = clips.data?.clips || [];
  if (clips.isError || rows.length === 0) return null;

  return (
    <Card className="p-4 mb-4" data-testid="study-clips">
      <div className="flex items-center gap-2 mb-2">
        <Bookmark className="h-4 w-4 text-indigo-600" />
        <p className="text-sm font-medium">Your saved clips</p>
        <span className="text-xs text-slate-500 ml-auto">{rows.length}</span>
      </div>
      <ul className="space-y-1.5">
        {rows.map((c) => (
          <li key={c.id} className="border rounded-lg px-3 py-2"
              data-testid={`study-clip-${c.id}`}>
            <div className="flex items-start gap-2">
            <button type="button" className="text-left min-w-0 flex-1 flex items-start gap-2"
                    onClick={() => navigate(
                      `/school/${c.tenant_id || tenantId}/live-classes/${c.session_id}`
                      + `?rec=${c.recording_id}&t=${Math.floor(c.start_seconds)}`)}
                    data-testid={`play-clip-${c.id}`}>
              <Play className="h-3.5 w-3.5 text-indigo-600 mt-1 shrink-0" />
              <span className="min-w-0">
                <span className="block text-xs text-slate-600 dark:text-slate-400">
                  {c.lesson} · {fmtClock(c.start_seconds)}–{fmtClock(c.end_seconds)}
                </span>
                <span className="block text-sm truncate">{c.label || `“${c.said}”`}</span>
              </span>
            </button>
            <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0"
                    onClick={() => remove.mutate(c.id)}
                    data-testid={`delete-clip-${c.id}`}>
              <Trash2 className="h-3.5 w-3.5 text-rose-600" />
            </Button>
            </div>

            {editing === c.id ? (
              <div className="mt-2 space-y-2">
                <Textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)}
                          placeholder="What this means, how it links to the essay, a question to ask…"
                          data-testid={`clip-note-input-${c.id}`} />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveNote(c)} disabled={savingNote}
                          data-testid={`clip-note-save-${c.id}`}>
                    {savingNote ? 'Saving…' : 'Save note'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="mt-1.5">
                {c.note ? (
                  <p className="text-sm whitespace-pre-wrap border-l-2 border-indigo-400 pl-2 text-slate-700 dark:text-slate-300"
                     data-testid={`clip-note-${c.id}`}>{c.note}</p>
                ) : null}
                <Button size="sm" variant="ghost" className="h-7 px-2 mt-1"
                        onClick={() => { setEditing(c.id); setDraft(c.note || ''); }}
                        data-testid={`clip-note-edit-${c.id}`}>
                  <PencilLine className="h-3.5 w-3.5 mr-1" />
                  {c.note ? 'Edit note' : 'Add a note'}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
};

export default StudyClips;
